import { CSSProperties, useCallback, useRef } from 'react'
import { Typography, styled } from '@mui/material'
import LinearProgress, {
  linearProgressClasses,
} from '@mui/material/LinearProgress'
import { RawAudioMessage } from '@weblite/ts-action/message'
import { playAudio, pauseAudio, useAudioState } from '@weblite/audio'
import * as audioPlayerService from '$logic/audio-player'
import { PlayIcon } from '$common/icons/play'
import { PauseIcon } from '$common/icons/pause'
import { ActionHandler, ActionHandlerState } from '$common/action-handler'
import { useSeek, useContentFileStatus } from '$common/react-hooks/message'
import { getFileTitle, formatFileSize } from '$func/file'
import { formatDuration } from '$func/time'
import { MessageMenuIcon } from '../../components/menu-icon'

const StyledLinearProgress = styled(LinearProgress)(({ theme, value }) => ({
  width: '100%',
  height: 4,
  borderRadius: 2,
  margin: '10px 0',
  /* @noflip */
  direction: 'ltr', // in every lang

  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: '#c4c4c4',
  },

  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 2,
    backgroundColor: theme.palette.secondary.main,
    transform: `translateX(-${100 - (value || 0)}%) !important`,
    transition: 'none',
  },
}))

const Container = styled('div')`
  display: flex;
  position: relative;
  align-items: center;

  padding: 10px 10px 10px 30px;
  box-sizing: border-box;
  overflow: hidden;
`

const ContentContainer = styled('div')`
  flex-grow: 1;
  /* @noflip */
  margin-right: 10px;
  overflow: hidden;
`

const AudioMetaTypography = styled(Typography)(({ theme }) => ({
  display: 'inline-block',
  fontSize: 11,
  color: theme.palette.gray,
  lineHeight: '12px',
}))

interface SeekThumbProps {
  value: number
}

const SeekThumb = styled('div')<SeekThumbProps>(({ theme, value }) => ({
  position: 'absolute',
  top: -4, // to be in the middle of progress bar
  /* @noflip */
  left: `clamp(0px, calc(${value}% - 6px), calc(100% - 12px))`,

  backgroundColor: theme.palette.secondary.main,
  width: 12,
  height: 12,
  borderRadius: '50%',

  boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.1)',
  cursor: 'pointer',
}))

const playStateToIconMap = {
  NOT_PLAYING: PlayIcon,
  PLAYING: PauseIcon,
  PAUSED: PlayIcon,
}

export function Audio({
  attachment,
  statusComp,
  uploadId,
  rootStyle,
  iid,
  roomId,
}: {
  attachment: RawAudioMessage['attachment']
  statusComp: JSX.Element | null
  uploadId: string
  rootStyle?: CSSProperties
  iid: number
  roomId: string
}) {
  const { duration, fileId, filename, cover, size } = attachment

  const {
    state,
    percentage,
    blobURL,
    cancelOperation,
    startDownload,
  } = useContentFileStatus({ fileId, uploadId, messageType: 'AUDIO' })

  console.log({ blobURL })

  const playState = useAudioState(blobURL)
  const containerRef = useRef<HTMLDivElement>(null)
  const seekContainerRef = useRef<HTMLDivElement>(null)

  const canSeek = state === 'AVAILABLE'

  const seekValue = useSeek({
    src: blobURL,
    canSeek,
    containerRef,
    seekContainerRef,
  })

  const downloadingOrUploading =
    state === 'UPLOADING' || state === 'DOWNLOADING'

  const { sizeText: fileSizeText, unit: fileSizeUnit } = formatFileSize(size)
  const { sizeText: progressedSizeText } = formatFileSize(
    Math.round(size * (percentage / 100)),
    fileSizeUnit,
    false,
  )

  // TODO: Use HostUrl
  const coverUrl = cover

  const audioTitle = getFileTitle(filename)
  const audioDurationText = formatDuration(duration)

  // in case of playing
  const spentDurationText = formatDuration(duration * seekValue)

  const CustomActionIcon =
    state === 'AVAILABLE' ? playStateToIconMap[playState] : undefined

  const togglePlay = useCallback(() => {
    if (playState === 'PLAYING') pauseAudio(blobURL)
    else {
      audioPlayerService.setData(roomId, iid)
      playAudio(blobURL, duration / 1000, { name: audioTitle, type: 'AUDIO' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playState, blobURL, duration, audioTitle])

  const seekPercentage = Math.round(seekValue * 100)

  return (
    <>
      <Container ref={containerRef} style={{ width: '280px', ...rootStyle }}>
        <ActionHandler
          state={state as ActionHandlerState}
          percentage={percentage}
          onCancelClick={cancelOperation}
          onDownloadClick={startDownload}
          onCustomActionIconClick={togglePlay}
          customActionIconComp={CustomActionIcon && <CustomActionIcon />}
          coverUrl={coverUrl}
        />

        <ContentContainer>
          <Typography
            color="semiBlack"
            variant="caption1"
            sx={{ display: 'block', lineHeight: '12px' }}
            noWrap
          >
            {audioTitle}
          </Typography>

          <div
            ref={seekContainerRef}
            style={{
              width: '100%',
              position: 'relative',
              cursor: canSeek ? 'pointer' : 'default',
            }}
          >
            <StyledLinearProgress
              variant="determinate"
              value={seekPercentage}
            />

            <SeekThumb
              value={seekPercentage}
              style={{
                display: canSeek ? 'unset' : 'none',
              }}
            />
          </div>

          <div
            style={{
              display: 'flex',
              overflow: 'hidden',
              alignItems: 'center',
            }}
          >
            {!downloadingOrUploading && (
              <>
                <AudioMetaTypography>{audioDurationText}</AudioMetaTypography>
                {playState !== 'NOT_PLAYING' && (
                  <AudioMetaTypography
                    sx={{
                      color: '#c4c4c4',
                      margin: '0 4px',
                    }}
                  >
                    /
                  </AudioMetaTypography>
                )}
              </>
            )}

            {(playState === 'PLAYING' || playState === 'PAUSED') && (
              <AudioMetaTypography>{spentDurationText}</AudioMetaTypography>
            )}
            {(!downloadingOrUploading ||
              playState === 'PLAYING' ||
              playState === 'PAUSED') &&
              state !== 'AVAILABLE' && (
                <AudioMetaTypography sx={{ marginX: '4px' }}>
                  /
                </AudioMetaTypography>
              )}

            {state !== 'AVAILABLE' && (
              <AudioMetaTypography
                noWrap
                sx={{
                  color: downloadingOrUploading ? 'primary.main' : 'gray',
                }}
              >
                {downloadingOrUploading
                  ? `${progressedSizeText} از ${fileSizeText}`
                  : fileSizeText}
              </AudioMetaTypography>
            )}
          </div>
        </ContentContainer>

        <MessageMenuIcon iid={iid} roomId={roomId} />
      </Container>
      {statusComp}
    </>
  )
}

Audio.defaultProps = {
  rootStyle: null,
}
